import { window } from "@swa-ui/browser/window";
import { swaDate } from "@swa-ui/date";

const ACTION_MAX_LENGTH = 200;
const COMPONENT_MAX_LENGTH = 100;
const ERROR_CODE_MAX_LENGTH = 9;
const ERROR_CODE_UI = "900000000";
const HTTP_CODE_REGEX = /^[0-9]{0,4}$/;
const LOCATION_MAX_LENGTH = 200;
const LOCATION_MIN_LENGTH = 1;
const LOG_LEVEL_VALUES = ["ERROR", "WARN", "INFO", "DEBUG", "TRACE", "FATAL", "ALL"];
const LOG_MESSAGE_FAILED_TO_FETCH = "Failed to fetch";
const LOG_UNIVERSAL_EXCEPTION = "UniversalException";
const LOG_FALLBACK_MESSAGE = "Unknown error";
const LOG_URL = "/api/logging/v1/logging/desktop/log";
const MESSAGE_MAX_LENGTH = 100;

const not = (any) => !any;

export const logger = {
  debug(message, context) {
    this.log(message, context, "DEBUG");
  },

  disable() {
    this.config.disabled = true;
  },

  enable() {
    this.config.disabled = false;
  },

  error(message, context) {
    this.log(message, context, "ERROR");
  },

  info(message, context) {
    this.log(message, context, "INFO");
  },

  init(config) {
    if (!this.config) {
      this.config = config;
    }
  },

  async log(message = LOG_FALLBACK_MESSAGE, context = {}, level = "INFO") {
    const {
      apiKey,
      appId,
      channelId,
      disabled,
      eventId,
      experienceId,
      url = LOG_URL,
    } = this.config;

    if (shouldLogRequest()) {
      const payload = getPayload();

      if (eventId) {
        window.QuantumMetricAPI?.sendEvent(eventId, 0, payload.message, payload);
      } else {
        try {
          await window.fetch(url, {
            body: JSON.stringify({ messages: [payload] }),
            headers: {
              "content-type": "application/json",
              "x-api-key": apiKey,
              "x-app-id": appId,
              "x-channel-id": channelId,
              "x-user-experience-id": experienceId,
            },
            method: "POST",
          });
        } catch (e) {
          console.error(e);
        }
      }
    }

    function shouldLogRequest() {
      return (
        not(
          message === LOG_MESSAGE_FAILED_TO_FETCH && context.component === LOG_UNIVERSAL_EXCEPTION
        ) && !disabled
      );
    }

    function getPayload() {
      return {
        action: getContextPathname(),
        component: context?.component?.slice(0, COMPONENT_MAX_LENGTH) ?? "",
        count: "1",
        details: getDetails(),
        errorCode: getErrorCode(true),
        httpCode: getHttpCode(true),
        level: getLevel(),
        location: getLocation(),
        message: message.slice(0, MESSAGE_MAX_LENGTH),
        timestamp: Math.floor(swaDate().valueOf() / 1000),
      };
    }

    function getLevel() {
      const upperCaseContextLevel = context?.level?.toUpperCase();
      const upperCaseLevel = level?.toUpperCase();

      return LOG_LEVEL_VALUES.includes(upperCaseContextLevel)
        ? upperCaseContextLevel
        : LOG_LEVEL_VALUES.includes(upperCaseLevel)
        ? upperCaseLevel
        : "";
    }

    function getDetails() {
      const buildDetails = () => ({
        callDuration: context.callDuration,
        code: context.code,
        errorCode: getErrorCode(),
        errorThrown: context.error,
        experienceId,
        httpCode: getHttpCode(),
        message: context.message,
        requestId: context.requestId,
        textStatus: context.statusText,
        url: context.url,
      });

      return JSON.stringify(context?.error?.stack ?? buildDetails());
    }

    function getErrorCode(shouldUseMaxLength = false) {
      const { codes = [""] } = context;
      const firstErrorCode = `${codes[0]}`;

      return shouldUseMaxLength ? firstErrorCode.slice(0, ERROR_CODE_MAX_LENGTH) : firstErrorCode;
    }

    function getHttpCode(shouldValidate = false) {
      let httpCode = context?.status ?? "";

      if (shouldValidate && httpCode !== "") {
        const isValidHttpCode = HTTP_CODE_REGEX.test(httpCode);

        httpCode = isValidHttpCode ? httpCode : "";
      }

      return httpCode;
    }

    function getLocation() {
      const pathname = window?.location?.pathname;
      const location = pathname?.split("/").filter(Boolean).join("/");

      return location?.length >= LOCATION_MIN_LENGTH
        ? location.slice(0, LOCATION_MAX_LENGTH)
        : undefined;
    }

    function getContextPathname() {
      const contextUrl = context?.url;

      return contextUrl ? new window.URL(contextUrl).pathname?.slice(0, ACTION_MAX_LENGTH) : null;
    }
  },

  warn(message, context) {
    this.log(message, context, "WARN");
  },
};
